import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router'

import type {
  Entry,
  FileEntry,
  FileRemap,
  FolderEntry,
} from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { $creatorId } from '@/models/creators.ts'
import { apiClient } from '@/utils/axios'

import type { ScriptNode } from '../types.ts'
import { ScriptSchema } from '../types.ts'

function fetchScriptById(creatorId: number, id: number) {
  return apiClient
    .get(`/settings/scripts/${id}?creatorId=${creatorId}`)
    .then((response) => ScriptSchema.parse(response.data))
}

export function useScript() {
  const creatorId = useUnit($creatorId)
  const { id } = useParams()
  const userId = id ? parseInt(id) : undefined

  return useQuery(
    ['script', userId],
    () => fetchScriptById(creatorId!, userId!),
    {
      enabled: !!userId && !!creatorId,
    },
  )
}

function isFileEntry(node: Entry<ScriptNode>): node is FileEntry<ScriptNode> {
  return node._type === 'file'
}

export function toTree(
  script: ScriptNode,
  parent: FolderEntry<ScriptNode> | null = null,
): Entry<ScriptNode> {
  const type = script.scripts === undefined ? 'file' : 'folder'

  return type === 'file'
    ? toFile(script, script.orderNumber, parent)
    : toFolder(script, script.orderNumber, script.scripts ?? [])
}

function toFile(
  script: ScriptNode,
  order: number,
  parent: FolderEntry<ScriptNode> | null = null,
): FileEntry<ScriptNode> {
  return Object.assign(script, {
    _type: 'file',
    _id: () => script.id,
    _order: () => order,
    _parent: () => parent,
    _withOrder: (newOrder: number) => toFile(script, newOrder, parent),
    orderNumber: order,
  } as const)
}

function mapChildren(
  parent: FolderEntry<ScriptNode>,
  nodes: ScriptNode[],
): FileEntry<ScriptNode>[] {
  return nodes
    .map((it) => toTree(it, parent))
    .filter(isFileEntry)
    .sort((left, right) => left.orderNumber - right.orderNumber)
}

function toFolder(
  script: ScriptNode,
  order: number,
  children: ScriptNode[],
): FolderEntry<ScriptNode> {
  const base: Partial<FolderEntry<ScriptNode>> = Object.assign(script, {
    _type: 'folder',
    _id: () => script.id,
    _order: () => order,
    _withOrder: (newOrder: number) => toFolder(script, newOrder, children),
  } as const)
  // We are doing a typecast here because of the circular dependency and the way
  // we have to build the object. It is totally okay to pass a partial node to the
  // child remapper because it only captures the reference.
  const fullReference = base as FolderEntry<ScriptNode>
  const childNodes = mapChildren(fullReference, children)

  base._listChildren = () => childNodes
  base._withChildren = (remap: FileRemap<ScriptNode>) =>
    toFolder(script, order, remap(childNodes))

  return fullReference
}
